import React, { useCallback, useEffect, useState } from "react";
import { Modal, Button, Form, InputGroup } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Icon } from "@iconify/react";
import useApi from "../../hook/useApi";
import { toast } from "react-toastify";
import Loader from "../Loader/Loader";
import { useAuth } from "../../context/AuthContext";
import SearchableSelect from "../Common/SearchableSelect";

const AddBrokerModal = ({ show, handleClose, editData, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { AsyncPostApiCall, AsyncPatchApiCall, AsyncGetApiCall } = useApi();
  const { subAdminId, profile } = useAuth();
  const isSubAdmin = !!subAdminId;
  const [subadminsList, setSubadminsList] = useState([]);
  const [selectedMaster, setSelectedMaster] = useState(null);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    loginId: Yup.string().required("Login ID is required"),
    password: Yup.string().when("isEdit", {
      is: false,
      then: (schema) =>
        schema
          .required("Password is required")
          .min(6, "Password must be at least 6 characters long")
          .matches(/^[0-9]+$/, "Only numbers are allowed"),
      otherwise: (schema) =>
        schema
          .min(6, "Password must be at least 6 characters long")
          .matches(/^[0-9]+$/, { message: "Only numbers are allowed", excludeEmptyString: true })
          .nullable(),
    }),
    confirmPassword: Yup.string().when("password", {
      is: (val) => val && val.length > 0,
      then: (schema) =>
        schema
          .required("Confirm Password is required")
          .oneOf([Yup.ref("password")], "Passwords must match"),
      otherwise: (schema) => schema.nullable(),
    }),
    remarks: Yup.string().nullable(),
    createdById: isSubAdmin
      ? Yup.string().nullable()
      : Yup.string().required("Subadmin is required"),
  });

  const [isLoginIdValid, setIsLoginIdValid] = useState(null);

  const generateLoginId = async () => {
    const result = await AsyncGetApiCall("/broker/generate-login-id");
    if (result?.data?.loginId) {
      formik.setFieldValue("loginId", result.data.loginId);
      setIsLoginIdValid(true);
    }
  };

  const checkLoginIdExists = async (val) => {
    if (!val || val.length !== 6) {
      setIsLoginIdValid(null);
      return;
    }
    const result = await AsyncGetApiCall("/broker/check-login-id", {
      loginId: val,
    });
    if (result?.data?.exists) {
      setIsLoginIdValid(false);
      formik.setFieldError("loginId", "Login ID already exists");
    } else {
      setIsLoginIdValid(true);
    }
  };

  const fetchMasterDetails = useCallback(
    async (masterId, setFormikSegments = true) => {
      // Used mainly in CREATE mode of when Master is explicitly changed
      if (!masterId) return;
      const result = await AsyncGetApiCall(
        `/subadmin/get-subadmin/${masterId}`,
      );
      console.log('result123', result?.data)
      if (result?.success) {
        setSelectedMaster(result.data);
        if (setFormikSegments) {
          const settings = {};
          Object.keys(result.data?.segmentSettings || {}).forEach((seg) => {
            if (result.data.segmentSettings[seg].enabled) {
              settings[seg] = {
                enabled: true,
                marginLimit: "",
                brokerageType:
                  result.data.segmentSettings[seg].brokerageType ||
                  "percentage",
                deliveryBrokerage: "",
                intradayBrokerage: "",
                brokerSettings: [],
              };
            }
          });
          formik.setFieldValue("segmentSettings", settings);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [AsyncGetApiCall],
  );

  useEffect(() => {
    if (show && !isSubAdmin) {
      (async () => {
        const result = await AsyncGetApiCall("/users/dynamic-list?listType=subadmin&inActiveSkip=true");
        if (result?.success) setSubadminsList(result.data || []);
      })();
    }
  }, [show, isSubAdmin, AsyncGetApiCall]);

  const formik = useFormik({
    initialValues: {
      name: "",
      loginId: "",
      password: "",
      confirmPassword: "",
      remarks: "",
      createdById: "",
      isEdit: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      const { confirmPassword, isEdit, ...payload } = values;
      if (!isEdit && isLoginIdValid === false) {
        toast.error("Please use a unique Login ID");
        return;
      }
      setLoading(true);
      let result;

      if (isSubAdmin) {
        payload.createdById = profile?._id;
      }

      if (isEdit) {
        if (!payload.password) delete payload.password;
        result = await AsyncPatchApiCall(`/broker/${editData._id}`, payload);
      } else {
        result = await AsyncPostApiCall(`/broker`, payload);
      }

      if (result?.success) {
        toast.success(
          editData
            ? "Broker updated successfully"
            : "Broker added successfully",
        );
        onSuccess();
        handleClose();
      } else {
        toast.error(result?.errors?.[0] || result?.message || "Something went wrong");
      }
      setLoading(false);
    },
  });

  const { isEdit } = formik.values;

  useEffect(() => {
    if (show) {
      if (editData) {
        formik.setValues({
          name: editData.name || "",
          loginId: editData.loginId || "",
          password: "",
          confirmPassword: "",
          remarks: editData.remarks || "",
          isEdit: true,
          createdById: editData.createdById?._id || editData.createdById || "",
        });
        setIsLoginIdValid(true);
      } else {
        formik.resetForm();
        formik.setFieldValue("isEdit", false);
        setIsLoginIdValid(null);
        generateLoginId();
      }
    }
  }, [editData, show]);

  return (
    <>
      <Modal show={show} onHide={handleClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editData ? "Edit Broker" : "Add New Broker"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={formik.handleSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Form.Label>
                  Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  placeholder="Enter name"
                  {...formik.getFieldProps("name")}
                  isInvalid={formik.touched.name && !!formik.errors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.name}
                </Form.Control.Feedback>
              </div>
              <div className="col-md-6 mb-3">
                <Form.Label>
                  Login ID <span className="text-danger">*</span>
                </Form.Label>
                <InputGroup hasValidation>
                  <Form.Control
                    type="text"
                    name="loginId"
                    placeholder="Enter 6-digit login ID"
                    {...formik.getFieldProps("loginId")}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      checkLoginIdExists(e.target.value);
                    }}
                    isInvalid={
                      formik.touched.loginId && !!formik.errors.loginId
                    }
                    disabled={!!editData}
                    maxLength={6}
                  />
                  {!editData && (
                    <Button
                      variant="outline-primary"
                      onClick={generateLoginId}
                      size="sm"
                    >
                      Auto
                    </Button>
                  )}
                  {isLoginIdValid !== null && !editData && (
                    <InputGroup.Text>
                      <Icon
                        icon={
                          isLoginIdValid
                            ? "lucide:check-circle"
                            : "lucide:x-circle"
                        }
                        className={
                          isLoginIdValid ? "text-success" : "text-danger"
                        }
                      />
                    </InputGroup.Text>
                  )}
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.loginId}
                  </Form.Control.Feedback>
                </InputGroup>
              </div>
              <div className="col-md-6 mb-3">
                <Form.Label>
                  Password{" "}
                  {editData && (
                    <span className="text-secondary-light text-sm">
                      (leave empty to keep current)
                    </span>
                  )}{" "}
                  {!editData && <span className="text-danger">*</span>}
                </Form.Label>
                <InputGroup hasValidation>
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Enter password"
                    {...formik.getFieldProps("password")}
                    isInvalid={
                      formik.touched.password && !!formik.errors.password
                    }
                  />
                  <InputGroup.Text
                    className="cursor-pointer"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <Icon
                      icon={showPassword ? "lucide:eye-off" : "lucide:eye"}
                    />
                  </InputGroup.Text>
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.password}
                  </Form.Control.Feedback>
                </InputGroup>
              </div>
              <div className="col-md-6 mb-3">
                <Form.Label>
                  Confirm Password{" "}
                  {(!editData ||
                    (formik.values.password &&
                      formik.values.password.length > 0)) && (
                      <span className="text-danger">*</span>
                    )}
                </Form.Label>
                <InputGroup hasValidation>
                  <Form.Control
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="Confirm password"
                    {...formik.getFieldProps("confirmPassword")}
                    isInvalid={
                      formik.touched.confirmPassword &&
                      !!formik.errors.confirmPassword
                    }
                  />
                  <InputGroup.Text
                    className="cursor-pointer"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    <Icon
                      icon={
                        showConfirmPassword ? "lucide:eye-off" : "lucide:eye"
                      }
                    />
                  </InputGroup.Text>
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.confirmPassword}
                  </Form.Control.Feedback>
                </InputGroup>
              </div>

              {!isSubAdmin && (
                <div className="col-md-12 mb-3">
                  <Form.Label>
                    Select Subadmin (Master) <span className="text-danger">*</span>
                  </Form.Label>
                  <SearchableSelect
                    value={formik.values.createdById}
                    name="createdById"
                    onChange={(e) => {
                      const val = e.target.value;
                      fetchMasterDetails(val, true);
                      formik.setFieldValue("createdById", val);
                      formik.setFieldTouched("createdById", true);
                    }}
                    onBlur={() => formik.setFieldTouched("createdById", true)}
                    placeholder="Select Subadmin..."
                    options={subadminsList.map((m) => ({
                      label: `${m.masterName || m.name} (${m.loginId})`,
                      value: String(m._id),
                    }))}
                    error={
                      formik.touched.createdById && formik.errors.createdById
                    }
                    disabled={isEdit}
                  />
                </div>
              )}

              <div className="col-12 mb-3">
                <Form.Label>Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  name="remarks"
                  rows={3}
                  placeholder="Enter remarks"
                  {...formik.getFieldProps("remarks")}
                  isInvalid={formik.touched.remarks && !!formik.errors.remarks}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.remarks}
                </Form.Control.Feedback>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={formik.handleSubmit}
            disabled={loading}
          >
            {loading ? "Saving..." : editData ? "Update Broker" : "Save Broker"}
          </Button>
        </Modal.Footer>
      </Modal>
      {loading && <Loader />}
    </>
  );
};

export default AddBrokerModal;
